import React, { useState, useMemo, useRef, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendDataPoint } from '../services/historicalAnalysisService';
import { CustomerTimeline, parseDate } from '../utils/historicalDataUtils';
import { ScaleFormat, DataPoint } from '@/types/base';
import { ProximityPointInfoBox } from '../../DistributionSection/ProximityPointInfoBox';
import { InfoRibbon } from '../../InfoRibbon/InfoRibbon';
import { Menu as MenuIcon, X } from 'lucide-react';
import { ChartColorPicker } from './ChartColorPicker';
import {
  describeTrendGranularity,
  isDateInTrendBucket
} from '../utils/trendAggregation';

interface TrendChartProps {
  data: TrendDataPoint[];
  timelines: CustomerTimeline[];
  scale: ScaleFormat;
  metric: 'satisfaction' | 'loyalty' | 'both';
  title: string;
  dateFormat?: string;
  /** Optional axis labels (defaults keep Review builds working without AxisLabelsContext) */
  satisfactionLabel?: string;
  loyaltyLabel?: string;
}

interface ClickedPoint {
  date: string;
  points: DataPoint[];
  metric: 'satisfaction' | 'loyalty';
  value: number;
  position: { x: number; y: number };
}

export const TrendChart: React.FC<TrendChartProps> = ({
  data,
  timelines,
  scale,
  metric,
  title,
  dateFormat,
  satisfactionLabel = 'Satisfaction',
  loyaltyLabel = 'Loyalty'
}) => {
  const labels = { satisfaction: satisfactionLabel, loyalty: loyaltyLabel };
  const [clickedPoint, setClickedPoint] = useState<ClickedPoint | null>(null);
  const [showControlsPanel, setShowControlsPanel] = useState(false);
  const [chartColors, setChartColors] = useState<{
    satisfactionColor?: string;
    loyaltyColor?: string;
    averageSatisfactionColor?: string;
    averageLoyaltyColor?: string;
  }>({
    satisfactionColor: '#3a863e',
    loyaltyColor: '#4682B4',
    averageSatisfactionColor: '#3a863e',
    averageLoyaltyColor: '#4682B4'
  });
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(320);
  
  // Close panel when clicking outside
  useEffect(() => {
    if (!showControlsPanel) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      const isPanelClick = targetElement.closest('.unified-controls-panel');
      const isControlButtonClick = settingsButtonRef.current?.contains(targetElement);
      
      // Close panel when clicking outside (but not on panel or button)
      if (!isPanelClick && !isControlButtonClick && panelRef.current) {
        setShowControlsPanel(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showControlsPanel]);

  // Measure plot width so dot radius can stay below half the gap to the next point
  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;

    const updateWidth = () => {
      // Subtract approximate left/right chart margins (20 + 30)
      setPlotWidth(Math.max(120, el.clientWidth - 50));
    };
    updateWidth();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Use pre-aggregated trend points (adaptive day/week/month/…) so large files
  // do not render thousands of overlapping dots.
  const customerLinesData = useMemo(() => data, [data]);

  /** Hard cap on radius so neighbouring dots never touch (gap ≥ 2px). */
  const maxDotRadius = useMemo(() => {
    const n = customerLinesData.length;
    if (n <= 1) return 5;
    const spacing = plotWidth / (n - 1);
    const radiusFromSpacing = (spacing - 2) / 2;
    return Math.max(2, Math.min(5, radiusFromSpacing));
  }, [customerLinesData.length, plotWidth]);

  const chartDataWithCustomers = useMemo(() => {
    const limitedTimelines = timelines.slice(0, 20);
    const useExactDateMatch = !data.some(p => p.granularity && p.granularity !== 'day');

    return customerLinesData.map((chartPoint, idx) => {
      const dataPoint: any = {
        date: chartPoint.date,
        dateIndex: idx,
        averageSatisfaction: chartPoint.averageSatisfaction,
        averageLoyalty: chartPoint.averageLoyalty,
        count: chartPoint.count
      };

      limitedTimelines.forEach((timeline, customerIdx) => {
        const customerPoint = timeline.dataPoints.find(p => {
          if (!p.date) return false;
          if (useExactDateMatch) {
            return p.date.trim() === chartPoint.date.trim();
          }
          const parsed = parseDate(p.date.trim(), dateFormat);
          return parsed
            ? isDateInTrendBucket(parsed, chartPoint.bucketStart, chartPoint.bucketEnd)
            : false;
        });

        if (customerPoint) {
          dataPoint[`customer_${customerIdx}_satisfaction`] = customerPoint.satisfaction;
          dataPoint[`customer_${customerIdx}_loyalty`] = customerPoint.loyalty;
        } else {
          dataPoint[`customer_${customerIdx}_satisfaction`] = null;
          dataPoint[`customer_${customerIdx}_loyalty`] = null;
        }
      });

      return dataPoint;
    });
  }, [customerLinesData, timelines, data, dateFormat]);

  const showIndividualLines = timelines.length <= 20;
  const granularity = data[0]?.granularity || 'day';
  const granularityLabel = describeTrendGranularity(granularity);

  // Determine Y-axis domain based on scale
  const getYAxisDomain = (scale: ScaleFormat): [number, number] => {
    const max = parseInt(scale.split('-')[1]);
    return [0, max];
  };

  const [yMin, yMax] = getYAxisDomain(scale);

  const resolveDataIndex = (payload: any, dotIndex?: number): number => {
    if (payload?.dateIndex !== undefined) return payload.dateIndex;
    if (dotIndex !== undefined && dotIndex >= 0 && dotIndex < customerLinesData.length) return dotIndex;
    if (payload?.date) {
      const payloadDate = payload.date.trim();
      return customerLinesData.findIndex(d => (d.date ? d.date.trim() : '') === payloadDate);
    }
    return -1;
  };

  // Handle point click — match customers whose dates fall in the chart bucket
  const handlePointClick = (_payload: any, index: number, metricType: 'satisfaction' | 'loyalty', event?: React.MouseEvent) => {
    const pointData = customerLinesData[index];
    if (!pointData) return;

    const customersAtDate: DataPoint[] = [];
    const useExactDateMatch = !pointData.granularity || pointData.granularity === 'day';

    timelines.forEach(timeline => {
      timeline.dataPoints.forEach(point => {
        if (!point.date) return;
        if (useExactDateMatch) {
          // Daily buckets use normalized dd/MM/yyyy labels — match by parsed calendar day
          const parsed = parseDate(point.date.trim(), dateFormat);
          if (parsed && pointData.bucketStart && pointData.bucketEnd) {
            if (isDateInTrendBucket(parsed, pointData.bucketStart, pointData.bucketEnd)) {
              customersAtDate.push(point);
            }
          } else if (point.date.trim() === pointData.date.trim()) {
            customersAtDate.push(point);
          }
          return;
        }

        const parsed = parseDate(point.date.trim(), dateFormat);
        if (parsed && isDateInTrendBucket(parsed, pointData.bucketStart, pointData.bucketEnd)) {
          customersAtDate.push(point);
        }
      });
    });

    if (customersAtDate.length > 0) {
      let position = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      if (event) {
        position = { x: event.clientX, y: event.clientY };
      } else {
        const chartElement = document.querySelector('.trend-chart-container');
        const rect = chartElement?.getBoundingClientRect();
        if (rect) {
          position = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          };
        }
      }

      setClickedPoint({
        date: pointData.date,
        points: customersAtDate,
        metric: metricType,
        value: metricType === 'satisfaction' ? pointData.averageSatisfaction : pointData.averageLoyalty,
        position
      });
    }
  };

  // Custom dot — size scales lightly with responses, but never overlaps neighbours
  const CustomDot = (props: any) => {
    const { cx, cy, payload, index: dotIndex } = props;
    // Recharts activeDot/dot typings require an Element, not null
    if (cx == null || cy == null) return <g />;

    const dataIndex = resolveDataIndex(payload, dotIndex);
    const pointData = dataIndex >= 0 ? customerLinesData[dataIndex] : null;
    const customerCount = pointData?.count || 1;
    const desiredRadius = 2.5 + Math.log2(customerCount + 1) * 0.55;
    const radius = Math.min(desiredRadius, maxDotRadius);
    const metricType = props.metricType || (props.dataKey?.includes('satisfaction') ? 'satisfaction' : 'loyalty');

    return (
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={props.fill}
        stroke={props.stroke}
        strokeWidth={1}
        style={{ cursor: 'pointer', pointerEvents: 'all' }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (dataIndex >= 0 && dataIndex < customerLinesData.length) {
            handlePointClick(payload, dataIndex, metricType, e);
          }
        }}
      />
    );
  };

  const metricLabel = metric === 'satisfaction' ? labels.satisfaction.toLowerCase() : metric === 'loyalty' ? labels.loyalty.toLowerCase() : metric;
  const groupingNote = granularity === 'day'
    ? 'Each point is the average of all responses on that date.'
    : `Dates are grouped ${granularityLabel} so the chart stays readable with large files. Each point is the average of all responses in that ${granularity === 'week' ? 'week' : granularity === 'month' ? 'month' : granularity === 'quarter' ? 'quarter' : 'year'}.`;
  const infoText = metric === 'both'
    ? `This chart shows how average ${labels.satisfaction.toLowerCase()} and ${labels.loyalty.toLowerCase()} change over time. ${groupingNote} Click any point to see which customers contributed to that average.`
    : `This chart shows how average ${metricLabel} changes over time. ${groupingNote} Click any point to see which customers contributed to that average.`;

  return (
    <>
      <div className="trend-chart-container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <h4 className="trend-chart-title" style={{ margin: 0 }}>{title}</h4>
          <button
            ref={settingsButtonRef}
            className={`trend-chart-settings-button ${showControlsPanel ? 'active' : ''}`}
            onClick={() => setShowControlsPanel(prev => !prev)}
            title="Chart settings"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '6px',
              background: showControlsPanel ? '#3a863e' : '#ffffff',
              border: '1px solid #e5e7eb',
              cursor: 'pointer',
              color: showControlsPanel ? '#ffffff' : '#3a863e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              if (!showControlsPanel) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showControlsPanel) {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <MenuIcon size={22} />
          </button>
        </div>
        <InfoRibbon text={infoText} />
        <div ref={chartAreaRef} style={{ width: '100%' }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartDataWithCustomers} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="date" 
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              domain={[yMin, yMax]}
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
              formatter={(value: number, name: string) => {
                return [value.toFixed(2), name === 'satisfaction' ? `${labels.satisfaction} (Avg)` : name === 'loyalty' ? `${labels.loyalty} (Avg)` : name];
              }}
              labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
            />
            <Legend 
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
            />
            
            {/* Individual customer lines - only show if not too many (performance) */}
            {showIndividualLines && timelines.slice(0, 20).map((timeline, idx) => {
              if (metric === 'satisfaction' || metric === 'both') {
                return (
                  <Line
                    key={`customer-${idx}-sat`}
                    type="monotone"
                    dataKey={`customer_${idx}_satisfaction`}
                    stroke="#cbd5e1"
                    strokeWidth={1}
                    dot={false}
                    connectNulls={true}
                    hide={true}
                    isAnimationActive={false}
                    legendType="none"
                  />
                );
              }
              if (metric === 'loyalty' || metric === 'both') {
                return (
                  <Line
                    key={`customer-${idx}-loy`}
                    type="monotone"
                    dataKey={`customer_${idx}_loyalty`}
                    stroke="#cbd5e1"
                    strokeWidth={1}
                    dot={false}
                    connectNulls={true}
                    hide={true}
                    isAnimationActive={false}
                    legendType="none"
                  />
                );
              }
              return null;
            })}
            
            {/* Average lines - prominent */}
            {(metric === 'satisfaction' || metric === 'both') && (
              <Line 
                type="monotone" 
                dataKey="averageSatisfaction" 
                stroke={chartColors.averageSatisfactionColor || '#3a863e'} 
                strokeWidth={3}
                dot={(props: any) => <CustomDot {...props} fill={chartColors.averageSatisfactionColor || '#3a863e'} stroke={chartColors.averageSatisfactionColor || '#3a863e'} dataKey="averageSatisfaction" metricType="satisfaction" />}
                name={`${labels.satisfaction} (Average)`}
                isAnimationActive={false}
                activeDot={(props: any) => {
                  const { payload, index: dotIndex, cx, cy } = props;
                  if (cx == null || cy == null) return <g />;
                  const dataIndex = resolveDataIndex(payload, dotIndex);
                  const satisfactionColor = chartColors.averageSatisfactionColor || '#3a863e';
                  const hoverRadius = Math.min(maxDotRadius + 1.5, 6);

                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={hoverRadius}
                      fill={satisfactionColor}
                      stroke={satisfactionColor}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                      onClick={(e: any) => {
                        e?.stopPropagation();
                        e?.preventDefault();
                        if (dataIndex >= 0 && dataIndex < customerLinesData.length) {
                          handlePointClick(payload, dataIndex, 'satisfaction', e);
                        }
                      }}
                    />
                  );
                }}
              />
            )}
            {(metric === 'loyalty' || metric === 'both') && (
              <Line 
                type="monotone" 
                dataKey="averageLoyalty" 
                stroke={chartColors.averageLoyaltyColor || '#4682B4'} 
                strokeWidth={3}
                dot={(props: any) => <CustomDot {...props} fill={chartColors.averageLoyaltyColor || '#4682B4'} stroke={chartColors.averageLoyaltyColor || '#4682B4'} dataKey="averageLoyalty" metricType="loyalty" />}
                name={`${labels.loyalty} (Average)`}
                isAnimationActive={false}
                activeDot={(props: any) => {
                  const { payload, index: dotIndex, cx, cy } = props;
                  if (cx == null || cy == null) return <g />;
                  const dataIndex = resolveDataIndex(payload, dotIndex);
                  const loyaltyColor = chartColors.averageLoyaltyColor || '#4682B4';
                  const hoverRadius = Math.min(maxDotRadius + 1.5, 6);

                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={hoverRadius}
                      fill={loyaltyColor}
                      stroke={loyaltyColor}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                      onClick={(e: any) => {
                        e?.stopPropagation();
                        e?.preventDefault();
                        if (dataIndex >= 0 && dataIndex < customerLinesData.length) {
                          handlePointClick(payload, dataIndex, 'loyalty', e);
                        }
                      }}
                    />
                  );
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
        </div>
        {!showIndividualLines && (
          <p className="trend-chart-note" style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            Note: Showing average only (too many customers to display individual lines)
            {granularity !== 'day' ? ` · Grouped ${granularityLabel}` : ''}
          </p>
        )}
        {showIndividualLines && granularity !== 'day' && (
          <p className="trend-chart-note" style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            Note: Points are grouped {granularityLabel} to keep the chart readable
          </p>
        )}
      </div>

      {/* Customer list modal */}
      {clickedPoint && (
        <ProximityPointInfoBox
          points={clickedPoint.points}
          position={clickedPoint.position}
          quadrant="loyalists"
          onClose={() => setClickedPoint(null)}
          context="distribution"
          customTitle={`Customers in ${clickedPoint.date} (${clickedPoint.metric}: ${clickedPoint.value.toFixed(2)})`}
        />
      )}
      
      {/* Unified Controls Panel */}
      {showControlsPanel && (
        <div className="unified-controls-panel trend-chart-controls-panel" ref={panelRef}>
          <div className="unified-controls-header">
            <div className="unified-controls-tabs">
              <div className="unified-tab active">
                <MenuIcon size={16} />
                Colors
              </div>
            </div>
            <button className="unified-close-button" onClick={() => setShowControlsPanel(false)}>
              <X size={20} />
            </button>
          </div>
          
          <div className="unified-controls-content">
            <div className="unified-tab-content">
              <div className="unified-tab-body">
                <div className="chart-settings-content">
                  {(metric === 'satisfaction' || metric === 'both') && (
                    <ChartColorPicker
                      label={`${labels.satisfaction} Color`}
                      currentColor={chartColors.averageSatisfactionColor || '#3a863e'}
                      onColorChange={(color) => setChartColors(prev => ({ ...prev, averageSatisfactionColor: color }))}
                    />
                  )}
                  {(metric === 'loyalty' || metric === 'both') && (
                    <ChartColorPicker
                      label={`${labels.loyalty} Color`}
                      currentColor={chartColors.averageLoyaltyColor || '#4682B4'}
                      onColorChange={(color) => setChartColors(prev => ({ ...prev, averageLoyaltyColor: color }))}
                    />
                  )}
                </div>
              </div>
              <div className="unified-tab-footer">
                <button 
                  className="unified-reset-button" 
                  onClick={() => {
                    setChartColors({
                      satisfactionColor: '#3a863e',
                      loyaltyColor: '#4682B4',
                      averageSatisfactionColor: '#3a863e',
                      averageLoyaltyColor: '#4682B4'
                    });
                  }}
                >
                  Reset to Default
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
